import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface AgendaConfig {
  instructor_id: string;
  working_days: string[];
  working_hours: Record<string, { start: string; end: string }>;
  default_duration: number;
  is_public: boolean;
  created_at?: string;
  updated_at?: string;
}

export const useAgendaConfig = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: config, isLoading, error } = useQuery({
    queryKey: ['agenda-config', user?.id],
    queryFn: async (): Promise<AgendaConfig> => {
      if (!user) throw new Error('No user');

      // Use the safe RPC function instead of direct table query
      const { data, error } = await supabase.rpc('get_instructor_agenda_config_safe', {
        target_instructor_id: user.id
      });

      if (error) throw error;

      // Check if data is empty array or null/undefined
      if (!data || (Array.isArray(data) && data.length === 0)) {
        // No configuration found, return defaults
        return {
          instructor_id: user.id,
          working_days: ['mon', 'tue', 'wed', 'thu', 'fri'], // Monday to Friday
          working_hours: {
            'mon': { start: '08:00', end: '18:00' }, // Monday
            'tue': { start: '08:00', end: '18:00' }, // Tuesday
            'wed': { start: '08:00', end: '18:00' }, // Wednesday
            'thu': { start: '08:00', end: '18:00' }, // Thursday
            'fri': { start: '08:00', end: '18:00' }  // Friday
          },
          default_duration: 60,
          is_public: false
        };
      }

      // RPC returns an array, so get the first item
      return Array.isArray(data) && data.length > 0 ? data[0] : data;
    },
    enabled: !!user,
  });

  const updateConfigMutation = useMutation({
    mutationFn: async (newConfig: Partial<AgendaConfig>) => {
      if (!user) throw new Error('No user');

      const configData = {
        instructor_id: user.id,
        ...newConfig,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('instructor_agenda_config')
        .upsert(configData, { onConflict: 'instructor_id' });

      if (error) throw error;
      return configData;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['agenda-config', user?.id] });
    },
  });

  return {
    config,
    isLoading,
    error: error?.message || null,
    updateConfig: updateConfigMutation.mutateAsync,
    isUpdating: updateConfigMutation.isPending
  };
};
